import { createNominationRefinement, deleteRefinement } from '@kudos/http-client'
import { useApiError } from '@shared/errors/useApiError'
import { useMutation } from '@tanstack/react-query'
import { isEmpty } from 'lodash/fp'
import { useRef, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import { NominateFormData } from './useNominateForm'

type UseNominationResponseRefinementArgs = {
  responseIndex: number
}

const useNominationResponseRefinement = ({
  responseIndex,
}: UseNominationResponseRefinementArgs) => {
  const [originalMessage, setOriginalMessage] = useState<string>('')
  const sessionIdRef = useRef<string | null>(null)

  const valueStringPath = `responses.${responseIndex}.valueString` as const
  const refinementIdPath = `responses.${responseIndex}.refinementId` as const

  const { control, setValue, setError } = useFormContext<NominateFormData>()
  const message = useWatch({ control, name: valueStringPath })
  const refinementId = useWatch({ control, name: refinementIdPath })

  const { handleApiError } = useApiError<NominateFormData>()

  const getSessionId = (): string => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = uuidv4()
    }
    return sessionIdRef.current
  }

  const { isPending: isPendingRefineMessage, mutateAsync: refineMessage } = useMutation({
    mutationFn: async () => {
      if (isEmpty(refinementId)) {
        setOriginalMessage(message ?? '')
      }

      return await createNominationRefinement({
        message: message ?? '',
        sessionId: getSessionId(),
      })
    },
    onSuccess: data => {
      setValue(refinementIdPath, data.refinementId, { shouldValidate: true })
      setValue(valueStringPath, data.output, { shouldValidate: true })
    },
    onError: error => {
      handleApiError(error, setError, [valueStringPath])
    },
  })

  const { isPending: isPendingUndoRefinedMessage, mutateAsync: deleteRefinementMessage } =
    useMutation({
      mutationFn: async () => {
        return await deleteRefinement(refinementId ?? '')
      },
      onSuccess: () => {
        setValue(refinementIdPath, undefined, { shouldValidate: true })
        setValue(valueStringPath, originalMessage, { shouldValidate: true })
      },
      onError: error => {
        handleApiError(error)
      },
    })

  return {
    isPendingRefineMessage,
    refineMessage,
    isPendingUndoRefinedMessage,
    deleteRefinementMessage,
    originalMessage,
    hasRefined: !!refinementId,
  }
}

export default useNominationResponseRefinement
